import * as classNames from 'classnames';
import { DataService } from '../../../services/data_service';
import { filterBundleForForm } from '../functions/filterBundle';
import { formStyle, WFormData } from '../../../core/forms/WFormFramework';
import { i18n } from '../../../i18n/i18n';
import { IDs } from '../../../helpers/IDs';
import { isValid as isValidData } from '../../../main/functions/validData';
import { lazyInject } from '../../../services/inversify';
import * as React from 'react';
import { removeErrorLevelAddOn } from '../../../core/forms/helpers/errorLevelTools';
import style from './DialogCO2Verbrauch.less';
import { validateFieldUniversal } from '../functions/validateField';
import { WActionButton } from '../../../core/elements/WActionButton';
import { WHelpIcon } from '../../../core/elements/WHelpIcon';
import { WSpinner } from '../../../core/elements/spinner/WSpinner';

export interface DialogCO2VerbrauchProps {
    data: any;
    entity: any;
    standortSelected: any;
    jahr: any;
    values: any;
    onChange: (entityName: string, formValues: any) => void;
    onClose: () => void;
    reloadData?: (entityName: string) => void;
}

export interface DialogCO2VerbrauchState {
    data: any;
    showSpinner: boolean;
}

export class DialogCO2Verbrauch extends React.Component<DialogCO2VerbrauchProps, DialogCO2VerbrauchState> {

    constructor(props: any) {
        super(props);
        this.state = {
            data: null,
            showSpinner: false
        };
        this.onChange = this.onChange.bind(this);
        this.saveData = this.saveData.bind(this);
    }

    @lazyInject(DataService) private dataService: DataService;

    private entity: string = (this.props.entity || {}).entity || null;
    private FormData: any = new WFormData();
    // private sum: number;

    componentDidMount(): void {
        const row: any = this.props.values || {};
        const data: any = {};
        (this.props.data[this.entity] || [])
            .filter((r: any) => r.parent_tid === (row.tid || 0) && r.modus === row.modus)
            .forEach((r: any, index: number) => data[index] = { tid: r.tid, verbrauch: r.verbrauch, energietraeger_tid: r.energietraeger_tid_orig });
        this.FormData.setBundle(this.props.data[`${this.entity}_bundle`]);
        this.setState({ data });
    }

    getValue(value: any): any {
        return typeof value !== 'undefined' && value !== null && !isNaN(+value) ? +value : null;
    }

    isValid(data: any): boolean {
        return isValidData(
            { verbrauch: data.verbrauch, energietraeger_tid: data.energietraeger_tid, modus: data.modus },
            this.props.entity.entity,
            this.props.data
        );
    }

    onChange(data: any): void {
        const index: number = typeof data.index === 'number' ? data.index : null;
        const value: any = data.value || null;
        const name: string = data.name || (this.state.data[index] || null);
        const values: any = this.state.data;
        values[index] = { ...values[index], [name]: value };
        this.setState({ data: values });
    }

    reloadData(data: any): void {
        // Speichere Jahresdaten durch onChange
        this.props.onChange(this.entity, data);
        if (typeof this.props.reloadData === 'function') {
            this.props.reloadData(this.entity);
        }
        this.props.onClose();
    }

    render(): JSX.Element {
        if (!this.state.data || [IDs.ENT_CO2_SCOPE3_TRANSPORT_VORGELAGERT, IDs.ENT_CO2_SCOPE3_REISEN, IDs.ENT_CO2_SCOPE3_PENDELN, IDs.ENT_CO2_SCOPE3_TRANSPORT_NACHGELAGERT].indexOf(this.entity) === -1) {
            return null;
        }
        return (
            <div className={classNames(formStyle.sidebysideComplexGreen, style.wrapper)}>
                {this.renderHeader()}
                {this.renderTable()}
                {this.renderButtons()}
                {this.state.showSpinner && <WSpinner />}
            </div>
        );
    }

    renderButtons(): JSX.Element {
        return (
            <div className={style.buttons}>
                <WActionButton
                    disabled={this.state.showSpinner}
                    label='Werte speichern und übernehmen'
                    insideText='Werte speichern und übernehmen'
                    styleProps={{ width: 250 }}
                    key='save'
                    action='save'
                    onAction={() => this.saveData()}
                />
                {/*<WActionButton
                    label='Schließen'
                    insideText='Schließen'
                    key='close'
                    action='delete'
                    onAction={this.props.onClose}
                />*/}
            </div>
        );
    }

    renderTable(): JSX.Element[] {
        const n: number = Object.keys(this.state.data || {}).length;
        const values: any = this.state.data || {};
        let rows: JSX.Element[] = [];
        // this.sum = this.calcSum();

        Object.keys(values).forEach((i: string) => {
            rows.push(
                <React.Fragment key={`row-${i}`}>{this.renderRow(+i)}</React.Fragment>
            );
        });
        if (n) {
            rows.push(
                <React.Fragment key={`row-${n}`}>{this.renderRow(n)}</React.Fragment>
            );
        } else {
            rows = [
                <React.Fragment key={`row-${n}`}>{this.renderRow(n)}</React.Fragment>,
                <React.Fragment key={`row-${n + 1}`}>{this.renderRow(n)}</React.Fragment>
            ];
        }

        return rows;
    }

    renderHeader(): JSX.Element {
        return (
            <div className={style.header}>
                <div>Kraftstoff</div>
                <div>Verbrauch</div>
                <div style={{ padding: '2px 0' }}>
                    {this.renderHilfeSymbol()}
                </div>
            </div>
        );
    }

    renderHilfeSymbol(): JSX.Element {
        return (
            <WHelpIcon
                data={this.props.data}
                styleDivProps={{ marginTop: 2, marginLeft: 10 }}
                styleProps={{ margin: 0 }}
                label='Rechner - Kraftstoffverbrauch'
                helpKey='dialog_rechner_kraftstoffverbrauch'
                style={'theme_small'}
                type={'theme_info'}
                customElement={'Info'}
                editBar
                useTheme
            />
        );
    }

    renderRow(index: number): JSX.Element {
        // console.log(this.FormData);
        const values: any = this.state.data[index] || {};
        let energietraeger: any[] = this.FormData.getBundleValues('energietraeger_tid');
        if (energietraeger && energietraeger.length > 0) {
            energietraeger = filterBundleForForm(this.entity, 'energietraeger_tid', energietraeger, null, this.props.standortSelected);
            energietraeger.sort((a: any, b: any) => ((a || {}).name || '').toString().localeCompare(((b || {}).name || '').toString()));
        }
        this.FormData.setAllValues({
            energietraeger_tid: this.getValue(values.energietraeger_tid),
            modus: this.getValue(values.modus) || (this.props.values || {}).modus || null,
            verbrauch: values.verbrauch || null
        });

        return (
            <div className={style.fields}>
                <div>
                    <select
                        name='energietraeger_tid'
                        value={+values.energietraeger_tid || null}
                        onChange={(evt: any) =>
                            this.onChange({ index, name: evt.target.name, value: evt.target.value === '0' ? null : evt.target.value })
                        }
                    >
                        {!energietraeger.length &&
                            <option>Nicht verfügbar</option>
                        }
                        {energietraeger.length > 1 &&
                            <option
                                key='energietraeger-'
                                value={0}
                            >
                                {i18n('keine Auswahl', 'sys')}
                            </option>
                        }
                        {energietraeger.map((item: any, index: number) =>
                            <option
                                key={`energietraeger-${index}`}
                                value={item.tid || undefined}
                            >
                                {item.name || null}
                            </option>
                        )}
                    </select>
                    {this.renderValidation('energietraeger_tid')}
                </div>
                <div>
                    <input
                        name='verbrauch'
                        type='number'
                        value={values.verbrauch || (values.verbrauch === 0 ? 0 : null)}
                        required
                        onChange={(evt: any) => {
                            this.onChange({ index, name: evt.target.name, value: evt.target.value });
                        }}
                    />
                    {this.renderValidation('verbrauch')}
                </div>
                {/* <div>
                    <WFormFieldArchiv
                        entity={this.entity}
                        label={null}
                        name='datei'
                        value={values.datei || 0}
                        data={values}
                        onValueChange={(name: string, value: any) => {
                            this.onChange({ index, name, value });
                        }}
                        module={IDs.MOD_ETOOL}
                        layout='noneAutoSave'
                    />
                </div> */}
                <div></div>
            </div>
        );
    }

    renderValidation(key: string): JSX.Element {
        const { text, type } = this.validateField(key);

        if (!text) {
            return null;
        }

        return (
            <div
                className={formStyle.warnSignDlgRechner}
                style={{ ...(type === 'warning' && { color: '#616468' }) }}
                title={text}
            >
                *
            </div>
        );
    }

    saveData(): void {
        const entityData: any[] = this.props.data[this.entity] || [];
        const row: any = this.props.values || {};
        const values: any = this.state.data || {};

        const jahr = this.props.jahr;
        const standort_tid = this.props.standortSelected.tid;
        const parent_tid = row.tid || 0;

        const data = entityData.filter((r: any) => r.parent_tid === parent_tid && r.modus === row.modus);

        const saveList: any[] = [];
        const delList: any[] = [];
        // const sum: number = this.sum !== null ? Math.round(this.sum * 100) / 100 : null;

        Object.keys(values).forEach((i: string) => {
            const value: any = values[i] || {};
            const datei = +value.datei || 0;
            const datei_archiv_delete = value.datei_archiv_delete || 0;
            const datei_archiv_filename = value.datei_archiv_filename || null;
            const datei_archiv_rawdata = value.datei_archiv_rawdata || null;
            const tid: number = +value.tid || null;
            const energietraeger_tid: string = value.energietraeger_tid || null;
            const verbrauch: number = +value.verbrauch || 0;
            const modus: string = row.modus || null;
            const found = data.find((item: any) => item.tid === (value.tid || null));

            const dataToSave: any = {
                energietraeger_tid,
                verbrauch,
                modus,
                datei,
                ...(datei_archiv_delete ? { datei_archiv_delete } : {}),
                ...(datei_archiv_filename ? { datei_archiv_filename } : {}),
                ...(datei_archiv_rawdata ? { datei_archiv_rawdata } : {})
            };
            if (found) {
                if (value.verbrauch === null && !datei && !datei_archiv_rawdata) {
                    delList.push({ tid });
                } else {
                    saveList.push({ tid, ...dataToSave });
                }
            } else if (verbrauch > 0 || datei > 0 || datei_archiv_rawdata) {
                saveList.push({ jahr, parent_tid, standort_tid, ...dataToSave });
            }
        });

        const valueYear: any = entityData.find((item: any) => item.tid === row.tid);
        Object.keys(this.FormData.bundle || {}).forEach((key: string) =>
            delete valueYear[key]
        );

        valueYear._formKey = row._formKey;
        // valueYear.verbrauch = sum;

        if (saveList.length > 0) {
            // Speichere Daten durch dataService.saveDataList
            this.setState({ showSpinner: true }, () => {
                this.dataService.saveDataList(this.entity, saveList, (sql: string, success: boolean) => {
                    this.setState({ showSpinner: false });
                    if (valueYear) { // Wenn Datensatz schon vorhanden --> immer speichern
                        delList.length
                            ? this.dataService.delDataList(this.entity, delList, () => this.reloadData(valueYear))
                            : this.reloadData(valueYear);
                    } else {
                        this.props.onClose();
                    }
                }, IDs.MOD_ETOOL);
            });
        } else if (delList.length > 0) {
            this.dataService.delDataList(this.entity, delList, () => this.reloadData(valueYear));
        } else {
            this.props.onClose();
        }
    }

    validateField(key: string): { text: string, type: string } {
        const name: string = (key || '').length ? (key || '').replace(/^\w/, (name: string) => name.toUpperCase()) : null;
        const energietraeger_tid: string = this.FormData.getValue('energietraeger_tid');
        const value: number | string = this.FormData.getValue(key);
        let text: string = null;
        let type: string = null;

        if (value === null && energietraeger_tid !== null) {
            text = `Bitte Feld "${name}" ausfüllen`;
            type = 'error';
        } else {
            text = removeErrorLevelAddOn(
                validateFieldUniversal(
                    this.props.entity.entity, key, value, this.FormData, null, this.props.data,
                    (data: any) => type = (data || {}).type || null
                )
            );
        }

        return { text, type };
    }

    // calcSum() {
    //     const { data, jahr } = this.props;
    //     let sum = 0;
    //     const formRows: any = this.state.data || {};
    //     const energietraeger: any[] = this.FormData.getBundleValues('energietraeger_tid');
    //     Object.keys(formRows).forEach((i: string) => {
    //         const row: any = { ...(formRows[i] || {}) };
    //         row.verbrauch = +row.verbrauch;
    //         row.energietraeger_tid = +row.energietraeger_tid;
    //         const etFnd = (energietraeger || []).find((r: any) => r.tid === row.energietraeger_tid);
    //         if (this.isValid(row) && !isNaN(row.verbrauch) && etFnd) {
    //             row.energietraeger_tid = etFnd.name;
    //             const fuel_item = getDataForKraftstoff(row, jahr, data);
    //             sum += fuel_item.co2 * 1000000;
    //         }
    //     });
    //     return sum;
    // }
}
